import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from "@mui/material";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import { useApiFetch } from "../api/apiClient";
import { colors } from "../utils/styles";
import { fmtDateTime } from "../utils/dateFormat";

export default function BlastEmail() {
  const apiFetch = useApiFetch();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected] = useState(() => new Set());
  const [pendingSend, setPendingSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [toast, setToast] = useState(null);
  const [parentFilters, setParentFilters] = useState([]);
  const [subagencyFilters, setSubagencyFilters] = useState([]);
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const parentOptions = useMemo(() => {
    if (!rows) return [];
    const seen = new Map();
    for (const r of rows) {
      for (const j of r.jurisdictions || []) {
        if (!j.parentJurisdictionCode || seen.has(j.parentJurisdictionCode)) continue;
        seen.set(j.parentJurisdictionCode, {
          code: j.parentJurisdictionCode,
          name: j.parentJurisdictionName || null,
          label: `${j.parentJurisdictionName || j.parentJurisdictionCode} (${j.parentJurisdictionCode})`
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const subagencyOptions = useMemo(() => {
    if (!rows) return [];
    const parentCodes = parentFilters.length ? new Set(parentFilters.map((p) => p.code)) : null;
    const seen = new Map();
    for (const r of rows) {
      for (const j of r.jurisdictions || []) {
        if (!j.jurisdictionCode || seen.has(j.jurisdictionCode)) continue;
        if (parentCodes && !parentCodes.has(j.parentJurisdictionCode)) continue;
        seen.set(j.jurisdictionCode, {
          code: j.jurisdictionCode,
          name: j.jurisdictionName || null,
          parentCode: j.parentJurisdictionCode || null,
          label: `${j.jurisdictionName || j.jurisdictionCode} (${j.jurisdictionCode})`
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, parentFilters]);

  // Drops any subagency selections that no longer belong to the selected
  // parent set, without re-deriving eligibility separately from subagencyOptions.
  useEffect(() => {
    setSubagencyFilters((prev) => {
      if (prev.length === 0) return prev;
      const validCodes = new Set(subagencyOptions.map((o) => o.code));
      const next = prev.filter((sf) => validCodes.has(sf.code));
      return next.length === prev.length ? prev : next;
    });
  }, [subagencyOptions]);

  // A recipient matches only if one single jurisdiction entry satisfies a
  // selected Parent and a selected Subagency together (not independently
  // across different entries on the same row).
  const filteredRows = useMemo(() => {
    if (!rows) return [];
    if (!parentFilters.length && !subagencyFilters.length) return rows;
    const parentSet = parentFilters.length ? new Set(parentFilters.map((p) => p.code)) : null;
    const subagencySet = subagencyFilters.length ? new Set(subagencyFilters.map((s) => s.code)) : null;
    return rows.filter((r) =>
      (r.jurisdictions || []).some(
        (j) =>
          (!parentSet || parentSet.has(j.parentJurisdictionCode)) &&
          (!subagencySet || subagencySet.has(j.jurisdictionCode))
      )
    );
  }, [rows, parentFilters, subagencyFilters]);

  const sortedRows = useMemo(() => {
    if (!sortBy) return filteredRows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      const aNull = av === null || av === undefined || av === "";
      const bNull = bv === null || bv === undefined || bv === "";
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (sortBy === "lastEmailSent") {
        return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
      }
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" }) * dir;
    });
  }, [filteredRows, sortBy, sortDir]);

  const eligibleEmails = useMemo(
    () => Array.from(new Set(sortedRows.filter((r) => r.email).map((r) => r.email))),
    [sortedRows]
  );

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  const onChangeParent = (v) => {
    setParentFilters(v);
    setPage(0);
  };

  const onChangeSubagency = (v) => {
    setSubagencyFilters(v);
    setPage(0);
  };

  const clearFilters = () => {
    setParentFilters([]);
    setSubagencyFilters([]);
    setPage(0);
  };

  const allSelected = eligibleEmails.length > 0 && selected.size === eligibleEmails.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleOne = (email) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleAll = () => {
    debugger;
    setSelected((prev) =>
      eligibleEmails.length > 0 && prev.size === eligibleEmails.length ? new Set() : new Set(eligibleEmails)
    );
  };

  const confirmSendSelected = async () => {
    setPendingSend(false);
    setSending(true);
    setBulkResult(null);
    const emails = Array.from(selected);
    try {
      const res = await apiFetch("/admin/blast-email/send-link-selected", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails })
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setBulkResult(data);
      const failedEmails = new Set((data.failed || []).map((f) => f.email));
      const nowIso = new Date().toISOString();
      setRows((prev) =>
        prev
          ? prev.map((row) =>
              row.email && selected.has(row.email) && !failedEmails.has(row.email)
                ? { ...row, lastEmailSent: nowIso }
                : row
            )
          : prev
      );
      setSelected(new Set());
      const failedCount = (data.failed || []).length;
      setToast({
        severity: failedCount === 0 ? "success" : "warning",
        message:
          failedCount === 0
            ? `Sent ${data.sent} access link${data.sent === 1 ? "" : "s"}.`
            : `Sent ${data.sent} of ${data.totalAttempted}. ${failedCount} failed.`
      });
    } catch (err) {
      setToast({ severity: "error", message: err.message || "Send failed." });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    apiFetch("/admin/blast-email")
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load access users.");
      });
    return () => {
      cancelled = true;
    };
  }, [apiFetch]);

  return (
    <Box sx={{ width: "100%", maxWidth: 800, mt: 2 }}>
      <Stack direction="row" sx={{ alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
          Online Preliminary Form Email Blast
        </Typography>
        <Button
          variant="contained"
          startIcon={<ForwardToInboxIcon />}
          onClick={() => setPendingSend(true)}
          disabled={selected.size === 0 || sending}
        >
          {sending ? "Sending..." : "Send Link to Selected Emails"}
        </Button>
      </Stack>

      {error && (
        <Box
          sx={{ p: 2, mb: 2, bgcolor: colors.errorBg, color: colors.error, borderRadius: 1, fontSize: 13 }}
        >
          Failed to load: {error}
        </Box>
      )}

      {!error && rows === null && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {rows && rows.length === 0 && (
        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>No access users found.</Box>
      )}

      {rows && rows.length > 0 && (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2, alignItems: { sm: "center" } }}
          >
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              options={parentOptions}
              value={parentFilters}
              onChange={(_, v) => onChangeParent(v)}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              sx={{ minWidth: 280, flex: 1 }}
              renderInput={(params) => <TextField {...params} label="Parent Agency" size="small" />}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              options={subagencyOptions}
              value={subagencyFilters}
              onChange={(_, v) => onChangeSubagency(v)}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              sx={{ minWidth: 280, flex: 1 }}
              renderInput={(params) => <TextField {...params} label="Subagency" size="small" />}
            />
            {(parentFilters.length > 0 || subagencyFilters.length > 0) && (
              <Button onClick={clearFilters}>Clear filters</Button>
            )}
          </Stack>

          {(parentFilters.length > 0 || subagencyFilters.length > 0) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Showing {filteredRows.length} of {rows.length} recipient{rows.length === 1 ? "" : "s"}.
            </Typography>
          )}
        </>
      )}

      {rows && rows.length > 0 && filteredRows.length === 0 && (
        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
          No recipients match the current filters.
        </Box>
      )}

      {rows && rows.length > 0 && filteredRows.length > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead
                sx={{
                  "& .MuiTableCell-head": { bgcolor: colors.darkGray, color: "white", fontWeight: 700 },
                  "& .MuiTableSortLabel-root": { color: "white !important" },
                  "& .MuiTableSortLabel-root.Mui-active": { color: "white !important" },
                  "& .MuiTableSortLabel-icon": { color: "white !important" }
                }}
              >
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      sx={{
                        color: "white",
                        "&.Mui-checked": { color: "white" },
                        "&.MuiCheckbox-indeterminate": { color: "white" }
                      }}
                      checked={allSelected}
                      indeterminate={someSelected}
                      onClick={toggleAll}
                      inputProps={{ "aria-label": "select all" }}
                    />
                  </TableCell>
                  {[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "lastEmailSent", label: "Last Email Sent", width: 180 }
                  ].map((c) => (
                    <TableCell
                      key={c.key}
                      sx={c.width ? { width: c.width } : undefined}
                      sortDirection={sortBy === c.key ? sortDir : false}
                    >
                      <TableSortLabel
                        active={sortBy === c.key}
                        direction={sortBy === c.key ? sortDir : "asc"}
                        onClick={() => toggleSort(c.key)}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r, idx) => {
                  const checked = !!r.email && selected.has(r.email);
                  return (
                    <TableRow
                      key={`${r.email}-${idx}`}
                      hover
                      selected={checked}
                      onClick={() => r.email && toggleOne(r.email)}
                      sx={{ cursor: r.email ? "pointer" : "default" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={checked}
                          disabled={!r.email}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => r.email && toggleOne(r.email)}
                          inputProps={{ "aria-label": `select ${r.email || "row"}` }}
                        />
                      </TableCell>
                      <TableCell>{r.name || "—"}</TableCell>
                      <TableCell>{r.email || "—"}</TableCell>
                      <TableCell>{fmtDateTime(r.lastEmailSent)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 25, 50, 100]}
            count={sortedRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}

      <Dialog open={pendingSend} onClose={() => setPendingSend(false)}>
        <DialogTitle>Send access link to selected recipients?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            An access link will be emailed to <strong>{selected.size}</strong> selected recipient
            {selected.size === 1 ? "" : "s"}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingSend(false)}>Cancel</Button>
          <Button onClick={confirmSendSelected} variant="contained">
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkResult !== null} onClose={() => setBulkResult(null)} fullWidth maxWidth="sm">
        <DialogTitle>Send results</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{bulkResult?.sent}</strong> of <strong>{bulkResult?.totalAttempted}</strong> sent.
          </Typography>
          {bulkResult && bulkResult.failed && bulkResult.failed.length > 0 && (
            <>
              <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 600 }}>
                Failed ({bulkResult.failed.length}):
              </Typography>
              <Box
                component="ul"
                sx={{
                  mt: 0.5,
                  pl: 3,
                  fontSize: 13,
                  color: "text.secondary",
                  maxHeight: 240,
                  overflow: "auto"
                }}
              >
                {bulkResult.failed.map((f, i) => (
                  <li key={i}>
                    <strong>{f.email}</strong> — {f.error}
                  </li>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkResult(null)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
